import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { veersaPointsDashboardStyles as styles } from "./constants/veersaPointsDashboard.styles";
import ActivitySubmissionDrilldown from "./ActivitySubmissionDrilldown";
import FacilitatingSessionsDrilldown from "./FacilitatingSessionsDrilldown";
import { VEERSA_POINTS_FACILITATING_ACTIVITY_CODE } from "./constants/veersaPointsConstants";
import { getVeersaPointsFacilitatingSessions } from "../../http/requests/veersa-points";

/** Mentoring PDFs served from UI/public (CRA root). */
const MENTOR_GUIDELINES_PDF = `/${encodeURIComponent("Veersa_MentorMentee_Guidelines.pdf")}`;
const MENTOR_ORIENTATION_PDF = `/${encodeURIComponent("Veersa_Mentor_Orientation  -  May 2026.pdf")}`;

const defaultFooter =
  "** Nomination but non-attendance does not earn points · Points approved by L&D before crediting";

function formatPoints(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return "0";
  return Math.abs(x - Math.round(x)) < 1e-6 ? String(Math.round(x)) : x.toFixed(1);
}

/**
 * Presentational Veersa Points dashboard — layout & styling aligned with VeersaDashboard.jsx sample.
 * Data comes from parent (API).
 */
export default function VeersaPointsDashboardView({
  loading = false,
  employeeName = "",
  employeeAzureId = "",
  pointsYear,
  yearOptions = [],
  onPointsYearChange,
  totalPoints = 0,
  annualTarget = 0,
  monthlyPoints = 0,
  monthlyDelta = 0,
  activitiesLogged = 0,
  categoriesCount = 0,
  pendingSubmissions = 0,
  activities = [],
  mySubmissions = [],
  submissionsLoading = false,
  sectionTitle = "Activity breakdown",
  footerNote,
  onLogActivity,
}) {
  const [hovered, setHovered] = useState(null);
  const [logRowHover, setLogRowHover] = useState(false);
  const [resourceHover, setResourceHover] = useState(null);
  const [expandedActivityId, setExpandedActivityId] = useState(null);
  const [infoBanner, setInfoBanner] = useState("");
  const [facilitatingRows, setFacilitatingRows] = useState([]);
  const [facilitatingLoading, setFacilitatingLoading] = useState(false);
  const [facilitatingError, setFacilitatingError] = useState("");

  useEffect(() => {
    if (!infoBanner) return undefined;
    const t = setTimeout(() => setInfoBanner(""), 6000);
    return () => clearTimeout(t);
  }, [infoBanner]);

  useEffect(() => {
    setExpandedActivityId(null);
  }, [pointsYear]);

  useEffect(() => {
    const act = (activities || []).find((x) => x.id === expandedActivityId);
    if (
      !expandedActivityId ||
      !act ||
      String(act.activityCode || "").toUpperCase() !== VEERSA_POINTS_FACILITATING_ACTIVITY_CODE
    ) {
      setFacilitatingRows([]);
      setFacilitatingLoading(false);
      setFacilitatingError("");
      return undefined;
    }
    if (!employeeAzureId) {
      setFacilitatingError("Missing signed-in user context.");
      return undefined;
    }
    let cancelled = false;
    setFacilitatingLoading(true);
    setFacilitatingError("");
    (async () => {
      try {
        const data = await getVeersaPointsFacilitatingSessions(pointsYear, employeeAzureId);
        if (!cancelled) {
          setFacilitatingRows(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setFacilitatingRows([]);
          const body = e?.data;
          setFacilitatingError(
            (typeof body?.errorMessage === "string" && body.errorMessage) ||
              (typeof body?.ErrorMessage === "string" && body.ErrorMessage) ||
              e?.message ||
              "Could not load facilitating sessions."
          );
        }
      } finally {
        if (!cancelled) {
          setFacilitatingLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expandedActivityId, activities, employeeAzureId, pointsYear]);
  const safeTarget = Math.max(0, Number(annualTarget) || 0);
  const safeTotal = Math.max(0, Number(totalPoints) || 0);
  const percent = safeTarget > 0 ? Math.min(100, Math.round((safeTotal / safeTarget) * 100)) : 0;
  const pointsToGo = Math.max(0, safeTarget - safeTotal);
  const deltaNum = Number(monthlyDelta) || 0;
  const deltaLabel = `${deltaNum > 0 ? "+" : ""}${formatPoints(deltaNum)} vs last month`;

  const invokeLog = () => {
    if (typeof onLogActivity === "function") {
      onLogActivity();
    }
  };

  const submissionsForActivity = useMemo(() => {
    if (!expandedActivityId || !Array.isArray(mySubmissions)) return [];
    return mySubmissions.filter((s) => String(s.activityId ?? s.ActivityId) === String(expandedActivityId));
  }, [expandedActivityId, mySubmissions]);

  const handleActivityRowClick = (a) => {
    if (!a.drillDownEnabled) {
      setInfoBanner(
        "Submission drill-down for Training and programs activities is not available yet. A tailored report will be added later."
      );
      return;
    }
    setExpandedActivityId((prev) => (prev === a.id ? null : a.id));
  };

  const handleActivityKeyDown = (e, a) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleActivityRowClick(a);
    }
  };

  const rowDrillTitle = (a) => {
    if (!a.drillDownEnabled) {
      return "Drill-down coming soon for this activity type";
    }
    if (String(a.activityCode || "").toUpperCase() === VEERSA_POINTS_FACILITATING_ACTIVITY_CODE) {
      return "Show completed sessions you facilitated";
    }
    return "Show Veersa Points submissions";
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div style={styles.headerTopRow}>
          <div style={styles.headerLeft}>
            <p style={styles.employeeTitle}>{loading && !employeeName ? "…" : employeeName || "—"}</p>
            {yearOptions.length > 0 && typeof onPointsYearChange === "function" ? (
              <select
                style={styles.yearSelect}
                aria-label="Points year"
                value={String(pointsYear)}
                onChange={(e) => onPointsYearChange(Number(e.target.value))}
                disabled={loading}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    FY{y}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          {onLogActivity ? (
            <button
              type="button"
              disabled={loading}
              aria-label="Log a new activity"
              style={{
                ...styles.headerLogRow,
                borderColor: logRowHover && !loading ? "#7c3aed" : "#a78bfa",
                background: logRowHover && !loading ? "#f3e8ff" : "#faf5ff",
                boxShadow:
                  logRowHover && !loading
                    ? "0 2px 10px rgba(91, 33, 182, 0.2)"
                    : "0 1px 3px rgba(91, 33, 182, 0.12)",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.55 : 1,
              }}
              onClick={invokeLog}
              onMouseEnter={() => setLogRowHover(true)}
              onMouseLeave={() => setLogRowHover(false)}
            >
              <div
                style={{
                  ...styles.actIcon,
                  background: "#ede9fe",
                  color: "#5b21b6",
                  fontWeight: 600,
                }}
              >
                +
              </div>
              <div style={styles.actInfo}>
                <p style={{ ...styles.actName, color: "#4c1d95", fontWeight: 600, margin: 0 }}>Log a new activity</p>
                <p style={{ ...styles.actMeta, color: "#6d28d9", margin: 0 }}>Certifications, mentoring, and content creation</p>
              </div>
              <div style={styles.actRight}>
                <p style={{ ...styles.actPts, color: "#5b21b6", fontWeight: 600, margin: 0 }}>0 pts</p>
              </div>
            </button>
          ) : null}
        </div>
      </div>

      <div style={styles.heroGrid}>
        <div style={styles.totalCard}>
          <p style={styles.totalLabel}>Total veersa points</p>
          <p style={styles.totalValue}>
            {loading ? "…" : formatPoints(safeTotal)}{" "}
            <span style={styles.totalValueSub}>/ {loading ? "…" : formatPoints(safeTarget)}</span>
          </p>
          <p style={styles.totalSub}>
            {loading ? "Loading…" : `${percent}% of annual target reached · ${formatPoints(pointsToGo)} points to go`}
          </p>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${loading ? 0 : percent}%` }} />
          </div>
        </div>

        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>This month</p>
          <p style={styles.metricValue}>{loading ? "…" : formatPoints(monthlyPoints)}</p>
          <p style={styles.metricSub}>{loading ? "—" : deltaLabel}</p>
        </div>

        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Activities logged</p>
          <p style={styles.metricValue}>{loading ? "…" : activitiesLogged}</p>
          <p style={styles.metricSub}>across {loading ? "…" : categoriesCount} categories</p>
        </div>

        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Pending review</p>
          <p style={styles.metricValue}>{loading ? "…" : pendingSubmissions}</p>
          <p style={styles.metricSub}>submissions awaiting L&amp;D</p>
        </div>
      </div>

      <p style={styles.sectionLabel}>{sectionTitle}</p>
      <div style={styles.activitiesList}>
        {infoBanner ? (
          <Box sx={{ mb: 1 }}>
            <Alert severity="info" onClose={() => setInfoBanner("")} sx={{ borderRadius: 2 }}>
              {infoBanner}
            </Alert>
          </Box>
        ) : null}
        {loading && (!activities || !activities.length) ? (
          <p style={{ ...styles.actMeta, padding: "0.5rem 0" }}>Loading activity breakdown…</p>
        ) : null}
        {(activities || []).map((a) => (
          <React.Fragment key={a.id}>
            <div
              role={a.drillDownEnabled ? "button" : undefined}
              tabIndex={a.drillDownEnabled ? 0 : undefined}
              title={rowDrillTitle(a)}
              style={{
                ...styles.actRow,
                background: hovered === a.id ? "#f9fafb" : "#fff",
                cursor: a.drillDownEnabled ? "pointer" : "default",
                outline: expandedActivityId === a.id ? "2px solid #c4b5fd" : undefined,
                outlineOffset: expandedActivityId === a.id ? 0 : undefined,
              }}
              onMouseEnter={() => setHovered(a.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => handleActivityRowClick(a)}
              onKeyDown={a.drillDownEnabled ? (e) => handleActivityKeyDown(e, a) : undefined}
              aria-expanded={a.drillDownEnabled ? expandedActivityId === a.id : undefined}
            >
              <div style={{ ...styles.actIcon, background: a.color.bg }}>{a.icon}</div>
              <div style={styles.actInfo}>
                <p style={styles.actName}>{a.name}</p>
                <p style={styles.actMeta}>{a.meta}</p>
              </div>
              <div style={{ ...styles.actRight, display: "flex", alignItems: "center", gap: 6 }}>
                {a.drillDownEnabled ? (
                  expandedActivityId === a.id ? (
                    <ExpandLess sx={{ color: "#6d28d9", fontSize: 22 }} aria-hidden />
                  ) : (
                    <ExpandMore sx={{ color: "#9ca3af", fontSize: 22 }} aria-hidden />
                  )
                ) : null}
                <p style={{ ...styles.actPts, margin: 0 }}>{formatPoints(a.pointsEarned)} pts</p>
              </div>
            </div>
            {expandedActivityId === a.id && a.drillDownEnabled ? (
              String(a.activityCode || "").toUpperCase() === VEERSA_POINTS_FACILITATING_ACTIVITY_CODE ? (
                <FacilitatingSessionsDrilldown
                  activityName={a.name}
                  rows={facilitatingRows}
                  loading={facilitatingLoading}
                  error={facilitatingError}
                />
              ) : (
                <ActivitySubmissionDrilldown
                  activityName={a.name}
                  submissions={submissionsForActivity}
                  loading={submissionsLoading}
                />
              )
            ) : null}
          </React.Fragment>
        ))}

        <div style={styles.resourcesBlock}>
          <p style={styles.resourcesLabel}>Mentoring resources</p>
          <a
            href={MENTOR_GUIDELINES_PDF}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.resourceLink,
              ...(resourceHover === "guidelines" ? styles.resourceLinkHover : null),
            }}
            onMouseEnter={() => setResourceHover("guidelines")}
            onMouseLeave={() => setResourceHover(null)}
          >
            <span style={styles.resourceIconWrap} aria-hidden>
              <MenuBookRoundedIcon sx={{ fontSize: 22 }} />
            </span>
            <span style={styles.resourceLinkText}>
              Veersa Mentor–Mentee Guidelines
              <span style={styles.resourceLinkSub}>
                <PictureAsPdfRoundedIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                PDF · Open guide
              </span>
            </span>
            <OpenInNewRoundedIcon sx={{ fontSize: 18, color: "#8b5cf6", flexShrink: 0 }} aria-hidden />
          </a>
          <a
            href={MENTOR_ORIENTATION_PDF}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.resourceLink,
              ...(resourceHover === "orientation" ? styles.resourceLinkHover : null),
            }}
            onMouseEnter={() => setResourceHover("orientation")}
            onMouseLeave={() => setResourceHover(null)}
          >
            <span style={styles.resourceIconWrap} aria-hidden>
              <SchoolRoundedIcon sx={{ fontSize: 22 }} />
            </span>
            <span style={styles.resourceLinkText}>
              Veersa Mentor Orientation — May 2026
              <span style={styles.resourceLinkSub}>
                <PictureAsPdfRoundedIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                PDF · Open orientation
              </span>
            </span>
            <OpenInNewRoundedIcon sx={{ fontSize: 18, color: "#8b5cf6", flexShrink: 0 }} aria-hidden />
          </a>
        </div>
      </div>

      <p style={styles.footer}>{footerNote ?? defaultFooter}</p>
    </div>
  );
}
